import { useRef } from "react";
import { type SafeArea, normalise } from "../site/bandLayout";

/**
 * TRACER LA ZONE À GARDER VISIBLE, SUR L'APERÇU DE L'IMAGE.
 *
 * ⚠️ ELLE REMPLACE LE POINT FOCAL du prototype (`HEADER_FOCAL` et sa grille de neuf
 * positions). Le point disait où viser ; il ne promettait rien. Un logo posé près du
 * bord disparaissait quand même sur un écran plus large, parce que `cover` rogne sans
 * regarder. La zone, elle, est une PROMESSE : le bandeau l'affiche entière, l'image
 * recule jusqu'à ce qu'elle tienne, et ce qui manque sur les côtés est peint de la
 * couleur du bord de l'image. Le calcul est dans `site/bandLayout.ts`, avec ses tests.
 *
 * L'aperçu montre l'image ENTIÈRE : on y trace le rectangle. Pas de second aperçu
 * recadré ici — le vrai bandeau est à deux centimètres, derrière le panneau, et il
 * suit en direct.
 *
 * ⚠️ SANS RÉGLAGE NI CONTEXTE. Il reçoit une zone et rend une zone : c'est ce qui
 * permet de le monter seul dans un banc et d'y vérifier les gestes, plutôt que de
 * juger un tracé sur une capture d'écran de production.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

/** Les quatre coins, dans l'ordre où ils se lisent. */
const CORNERS = ["nw", "ne", "sw", "se"] as const;
type Corner = (typeof CORNERS)[number];
type Gesture = "draw" | "move" | Corner;

/** Sous ça, ce n'était pas un tracé mais un clic : la zone est retirée. */
export const MIN = 0.02;

/** Le pas du clavier, en fractions de l'image. */
const STEP = 0.02;

const clamp = (n: number, low: number, high: number) =>
  Math.min(high, Math.max(low, n));

/** En pourcentage entier, pour ce qui est dit à voix haute. */
const percent = (n: number) => Math.round(n * 100);

/**
 * Le coin QUI NE BOUGE PAS pendant un redimensionnement : celui d'en face. Ramener
 * un coin à son opposé fait du redimensionnement un tracé comme un autre, et il n'y
 * a plus qu'une seule géométrie à tenir juste.
 */
function anchorOf(corner: Corner, area: SafeArea): { x: number; y: number } {
  return {
    x: corner === "nw" || corner === "sw" ? area.x + area.w : area.x,
    y: corner === "nw" || corner === "ne" ? area.y + area.h : area.y,
  };
}

/** Le rectangle entre deux points, quel que soit l'ordre dans lequel on les donne. */
function between(
  a: { x: number; y: number },
  b: { x: number; y: number },
): SafeArea {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(b.x - a.x),
    h: Math.abs(b.y - a.y),
  };
}

export function SafeAreaPicker({
  url,
  label,
  area,
  onChange,
  onClear,
  onBroken,
  onMeasured,
}: {
  url: string;
  label: string;
  /** La zone en place, ou rien tant qu'aucune n'a été tracée. */
  area: SafeArea | null;
  onChange: (area: SafeArea) => void;
  onClear: () => void;
  /** L'image ne se charge plus : c'est la ligne, pas le sélecteur, qui le dit. */
  onBroken: () => void;
  /** L'image est chargée : ses dimensions RÉELLES, en pixels du fichier. */
  onMeasured?: (size: { width: number; height: number }) => void;
}) {
  const frame = useRef<HTMLDivElement>(null);

  const put = (next: SafeArea) => onChange(normalise(next));

  /** Où le pointeur est tombé DANS L'IMAGE, en fractions d'elle-même. */
  const at = (point: { clientX: number; clientY: number }, box: DOMRect) => ({
    x: clamp((point.clientX - box.left) / box.width, 0, 1),
    y: clamp((point.clientY - box.top) / box.height, 0, 1),
  });

  /**
   * LE GESTE — tracer, déplacer, redimensionner, avec le même code.
   *
   * ⚠️ IL ÉCOUTE LA FENÊTRE, PAS L'APERÇU. Mesuré sur le point focal, et la raison
   * vaut toujours : `setPointerCapture` tombe au premier rendu déclenché par
   * l'écriture, et le geste s'arrêtait au premier mouvement. Sur la fenêtre, il
   * continue aussi quand le pointeur sort de l'image — ce qui compte, puisque c'est
   * souvent le BORD qu'on cherche à attraper.
   */
  const begin = (gesture: Gesture, event: React.PointerEvent) => {
    // Un geste sur la zone ou sur une poignée n'est pas un nouveau tracé sur le fond.
    event.stopPropagation();
    event.preventDefault();
    const box = frame.current?.getBoundingClientRect();
    if (!box) return;

    const start = area ?? { x: 0, y: 0, w: 0, h: 0 };
    const origin = at(event, box);
    const anchor = gesture === "draw" ? origin : anchorOf(gesture as Corner, start);
    let last = start;

    const apply = (point: { clientX: number; clientY: number }) => {
      const now = at(point, box);
      last =
        gesture === "move"
          ? {
              ...start,
              x: clamp(start.x + (now.x - origin.x), 0, 1 - start.w),
              y: clamp(start.y + (now.y - origin.y), 0, 1 - start.h),
            }
          : between(anchor, now);
      put(last);
    };

    if (gesture === "draw") apply(event);

    const move = (moved: PointerEvent) => apply(moved);
    const release = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
      // Un clic sec sur l'image n'est pas une zone d'un pixel : c'est un retrait.
      if (last.w < MIN || last.h < MIN) onClear();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
  };

  /** Flèches : déplacer. Maj + flèches : agrandir ou réduire par le coin bas-droit. */
  const onKeys = (event: React.KeyboardEvent) => {
    if (!area) return;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-STEP, 0],
      ArrowRight: [STEP, 0],
      ArrowUp: [0, -STEP],
      ArrowDown: [0, STEP],
    };
    const step = moves[event.key];
    if (!step) return;
    event.preventDefault();
    if (event.shiftKey)
      put({
        ...area,
        w: clamp(area.w + step[0], MIN, 1 - area.x),
        h: clamp(area.h + step[1], MIN, 1 - area.y),
      });
    else
      put({
        ...area,
        x: clamp(area.x + step[0], 0, 1 - area.w),
        y: clamp(area.y + step[1], 0, 1 - area.h),
      });
  };

  return (
    /* Un `div` et non un bouton : il contient lui-même des boutons (la zone, ses
       poignées), et un bouton dans un bouton n'existe pas. Ce qui se manipule au
       clavier, c'est la zone. */
    <div
      ref={frame}
      className="upl__pick"
      onPointerDown={(event) => begin("draw", event)}
    >
      <img
        className="upl__prev"
        src={url}
        alt={`Aperçu : ${label}`}
        /**
         * ⚠️ `draggable={false}` FAIT LE GLISSÉ. Sans lui, le deuxième mouvement
         * déclenchait le glisser-déposer d'image du navigateur, qui envoie un
         * `pointercancel` : le geste était annulé et la zone restait où le premier
         * mouvement l'avait mise. Mesuré sur le point focal.
         */
        draggable={false}
        onError={onBroken}
        onLoad={(event) =>
          onMeasured?.({
            width: event.currentTarget.naturalWidth,
            height: event.currentTarget.naturalHeight,
          })
        }
      />
      {area && (
        <button
          type="button"
          className="upl__safe"
          style={{
            left: `${area.x * 100}%`,
            top: `${area.y * 100}%`,
            width: `${area.w * 100}%`,
            height: `${area.h * 100}%`,
          }}
          aria-label={`Zone à garder visible : ${percent(area.x)} % depuis la gauche, ${percent(area.y)} % depuis le haut, ${percent(area.w)} % de large, ${percent(area.h)} % de haut. Flèches pour la déplacer, Maj et flèches pour la redimensionner.`}
          onPointerDown={(event) => begin("move", event)}
          onKeyDown={onKeys}
        >
          {CORNERS.map((corner) => (
            <span
              key={corner}
              className={`upl__grip upl__grip--${corner}`}
              onPointerDown={(event) => begin(corner, event)}
            />
          ))}
        </button>
      )}
    </div>
  );
}
